<template>
  <b-modal 
    id="modal-add-participant" 
    centered 
    hide-footer 
    title="Add Participant"
    size="lg">
    <form @submit.prevent="saveAdd()">
      <div class="row">
        <div class="col-md-12">
          <div class="form-group">
            <label>Search Employee by NIK or Name <span class="text-danger">*</span></label>
            <v-select
              v-model="selectedEmployee"
              :options="searchedEmployees"
              :reduce="option => option"
              label="full_name"
              placeholder="Search by NIK or Name..."
              @search="onSearchEmployee"
              @input="onEmployeeSelected"
              :filterable="false"
              :loading="isSearching">
              <template #no-options="{ searching }">
                <template v-if="!searching">
                  Type at least 2 characters to search...
                </template>
                <template v-else-if="searchedEmployees.length === 0 && searching">
                  No employees found
                </template>
                <em v-else>Search Fullname or NIK</em>
              </template>
              <template #option="option">
                <div>
                  <div class="font-weight-bold">{{ option.full_name }}</div>
                  <small>NIK: {{ option.nik }} | Position: {{ option.position || '-' }}</small>
                </div>
              </template>
            </v-select>
            <div
              v-if="syncFeedback.message"
              class="alert mt-2 mb-0 py-2"
              :class="syncFeedback.className">
              <span
                v-if="syncFeedback.loading"
                class="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"></span>
              {{ syncFeedback.message }}
            </div>
          </div>
        </div>

        <!-- Employee Card -->
        <div v-if="selectedEmployee" class="col-md-12">
          <div class="card mb-3">
            <div class="card-body">
              <div class="d-flex align-items-start">
                <div class="user-avatar me-3">
                  <div class="avatar-circle">
                    <i class="fe fe-user"></i>
                  </div>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex justify-content-between align-items-start">
                    <div>
                      <h5 class="mb-1">{{ selectedEmployee.full_name }}</h5>
                      <p class="text-muted mb-2">{{ selectedEmployee.position || '-' }}</p>
                    </div>
                    <span class="badge badge-primary">{{ selectedEmployee.nik }}</span>
                  </div>
                  <div class="user-details mt-3">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>NIK</strong>
                          <p>{{ selectedEmployee.nik }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>Position</strong>
                          <p>{{ selectedEmployee.position || '-' }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>Email</strong>
                          <p>{{ selectedEmployee.email || '-' }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="detail-item">
                          <strong>Company</strong>
                          <p>{{ selectedEmployee.company || '-' }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="form-group text-right mt-4">
        <button
          type="button"
          class="btn btn-secondary me-2"
          @click="closeModal">
          Cancel
        </button>
        <button
          class="btn btn-danger"
          type="submit"
          :disabled="!selectedEmployee">
          <i class="fe fe-plus me-1"></i>
          Add Participant
        </button>
      </div>
    </form>
  </b-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';

export default {
  name: 'ModalAddParticipant',
  props: {
    batchId: {
      type: [Number, String],
      default: null
    }
  },
  computed: {
    ...mapGetters('courseCatalog', {
      searchedEmployees: 'getSearchedEmployees'
    })
  },
  data() {
    return {
      selectedEmployee: null,
      searchQuery: '',
      isSearching: false,
      searchTimer: null,
      searchRequestId: 0,
      syncFeedback: {
        loading: false,
        message: '',
        className: 'alert-info'
      }
    }
  },
  methods: {
    ...mapActions('courseCatalog', ['searchEmployees', 'syncEmployeeOnDemand', 'addParticipant']),
    
    onSearchEmployee(search) {
      clearTimeout(this.searchTimer);
      const requestId = ++this.searchRequestId;
      this.syncFeedback = { loading: false, message: '', className: 'alert-info' };

      if (search.length >= 2) {
        this.searchQuery = search;
        this.isSearching = true;
        this.searchTimer = setTimeout(() => this.performSearch(search, requestId), 400);
      } else {
        this.searchQuery = '';
        this.isSearching = false;
      }
    },

    async performSearch(search, requestId) {
      try {
        const employees = await this.searchEmployees(search);
        if (requestId !== this.searchRequestId) return;

        const exactNikFound = employees.some(
          employee => this.normalizeNik(employee.nik) === this.normalizeNik(search)
        );

        if (!exactNikFound && this.looksLikeNik(search)) {
          this.syncFeedback = {
            loading: true,
            message: 'NIK tidak ditemukan di lokal. Memeriksa Portal...',
            className: 'alert-info'
          };

          const result = await this.syncEmployeeOnDemand(search.trim());
          if (requestId !== this.searchRequestId) return;

          if (result?.employee) {
            const syncedEmployee = (result.employees || []).find(
              employee => this.normalizeNik(employee.nik) === this.normalizeNik(search)
            );

            if (syncedEmployee) {
              this.selectedEmployee = syncedEmployee;
              this.syncFeedback = { loading: false, message: '', className: 'alert-info' };
              return;
            }
          }

          const feedbackClass = result?.status === 'inactive' || result?.status === 'not_syncable'
            ? 'alert-warning'
            : 'alert-info';

          this.syncFeedback = {
            loading: false,
            message: result?.message || 'Sinkronisasi user selesai.',
            className: feedbackClass
          };
        }
      } catch (error) {
        console.error('Error searching employees:', error);
        if (requestId === this.searchRequestId) {
          this.syncFeedback = {
            loading: false,
            message: error.response?.data?.message || 'Pencarian user belum berhasil. Silakan coba kembali.',
            className: 'alert-danger'
          };
        }
      } finally {
        if (requestId === this.searchRequestId) {
          this.isSearching = false;
        }
      }
    },

    normalizeNik(value) {
      return String(value || '').trim().toUpperCase();
    },

    looksLikeNik(value) {
      return /^(?:[A-Z]+-)?\d{4,}$/i.test(String(value || '').trim());
    },

    onEmployeeSelected(employee) {
      console.log('✅ Employee selected:', employee);
      this.selectedEmployee = employee;
    },

    async saveAdd() {
      if (!this.selectedEmployee) {
        this.$swal('Error', 'Pilih karyawan terlebih dahulu', 'error');
        return;
      }

      if (!this.batchId) {
        this.$swal('Error', 'ID batch wajib diisi', 'error');
        return;
      }

      // Show confirmation dialog
      const result = await this.$swal({
        title: 'Tambah Peserta?',
        html: `
          <p>Apakah Anda yakin ingin menambahkan <strong>${this.selectedEmployee.full_name}</strong> (NIK: ${this.selectedEmployee.nik}) sebagai peserta?</p>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Ya, tambah',
        cancelButtonText: 'Batal',
        confirmButtonColor: '#e63757',
        cancelButtonColor: '#6c757d',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            // Add participant via API with only nik in payload
            await this.addParticipant({
              trainingEventId: this.batchId,
              data: {
                nik: this.selectedEmployee.nik
              }
            });
            return { success: true };
          } catch (error) {
            this.$swal.showValidationMessage(
              `Peserta belum berhasil ditambahkan: ${error.response?.data?.error || error.response?.data?.message || error.message}`
            );
            return { success: false };
          }
        },
        allowOutsideClick: () => !this.$swal.isLoading()
      });

      if (result.isConfirmed && result.value.success) {
        // Show success message first
        await this.$swal({
          title: 'Success',
          text: `${this.selectedEmployee.full_name} berhasil ditambahkan sebagai peserta`,
          icon: 'success',
          confirmButtonColor: '#e63757'
        });
        
        // After user acknowledges, close modal and emit event
        this.closeModal();
        this.$emit('participant-added');
      }
    },

    resetForm() {
      clearTimeout(this.searchTimer);
      this.searchRequestId += 1;
      this.selectedEmployee = null;
      this.searchQuery = '';
      this.isSearching = false;
      this.syncFeedback = { loading: false, message: '', className: 'alert-info' };
    },

    closeModal() {
      this.resetForm();
      this.$bvModal.hide('modal-add-participant');
    }
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer);
    this.resetForm();
  }
}
</script>

<style scoped>
.user-avatar {
  flex-shrink: 0;
}

.user-avatar .avatar-circle {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #e63757 0%, #f56e86 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.badge-primary {
  background-color: #e63757;
  color: white;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.detail-item {
  margin-bottom: 1rem;
}

.detail-item strong {
  display: block;
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.25rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-item p {
  margin: 0;
  font-size: 0.875rem;
  color: #2d3748;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.15em;
}
</style>
